// marked 15.x is the last release with a require-able CommonJS entry point;
// v16 and later ship ESM-only, and CRA's Jest config does not transform
// node_modules by default, so bumping past 15.x makes the whole client suite
// fail to load rather than fail a test. Keep the version pinned to 15.x.
import { marked } from 'marked';
import createDOMPurify from 'dompurify';

// Guidelines text is Markdown an admin typed and every viewer's browser
// renders it straight into the DOM, on the guidelines page and the
// post-form panel. This allow-list is the actual security boundary between a
// saved <script> tag and every member's session, so no caller may insert
// guidelines text without going through renderMarkdown first.
//
// h1 is deliberately absent: every page that renders this text already
// carries its own page-title <h1> (GuidelinesPage's "Reuse guidelines"), and
// a second one breaks the document outline. An omitted tag is stripped while
// its text is kept, so a heading typed at the top of the guidelines renders
// as plain text rather than demoting to h2.
const ALLOWED_TAGS = [
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'p',
    'ul',
    'ol',
    'li',
    'strong',
    'em',
    'a',
    'code',
    'pre',
    'blockquote',
    'br',
    'hr',
    'del',
    'table',
    'thead',
    'tbody',
    'tr',
    'th',
    'td',
];
// No `target`: nothing in the rendered output sets one, so the only way the
// attribute could arrive is a raw <a> typed into the guidelines, and that
// would let the text decide where a member's click lands.
const ALLOWED_ATTR = ['href', 'rel'];

// A dedicated instance rather than the module-level default DOMPurify
// export. That default is a shared singleton: a hook registered on it as an
// import side effect would reach every future sanitize call anywhere in the
// bundle, including a caller whose own ALLOWED_ATTR excludes what the hook
// sets. Hooks run after attribute sanitization, so they write past whatever
// allow-list that caller configured. This file is the security boundary and
// must not reach outside itself.
const purify = createDOMPurify(window);

// marked's default renderer emits a bare <a href> with no rel. Adding it
// here as a hook on this dedicated instance, rather than a custom marked
// renderer, means every link gets it regardless of how the html arrived, and
// it survives the sanitize step that follows immediately after.
purify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A') {
        node.setAttribute('rel', 'noopener noreferrer');
    }
});

export function renderMarkdown(text) {
    const html = marked.parse(text || '', { gfm: true });
    return purify.sanitize(html, { ALLOWED_TAGS, ALLOWED_ATTR });
}

export default renderMarkdown;
